/**
 * The Decision Inbox — the real one
 *
 * Every card here came from /api/core/cards. Nothing is demo content.
 *
 * Sorted by value at stake rather than by age, because an inbox sorted by age
 * trains people to work the queue instead of the business.
 *
 * The card detail shows the arithmetic. That is the point of the screen: a
 * CEO should be able to disagree with the working rather than with the
 * conclusion, and a figure nobody can check is a figure they are right to
 * refuse.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, PageHead, Note, Tag, Tone, Split } from '../../components/ui';
import {
  CardDetail,
  CardSummary,
  cardsService,
  formatEgp,
} from '../services/cards.service';

const CONFIDENCE_TONE: Record<string, Tone> = { high: 'g', medium: 'a', low: 'r' };

export const DecisionInboxPage: React.FC = () => {
  const [cards, setCards] = useState<CardSummary[]>([]);
  const [open, setOpen] = useState<CardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setCards(await cardsService.listPending());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the inbox.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCard = useCallback(async (reference: string) => {
    setError(null);
    try {
      setOpen(await cardsService.getCard(reference));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that card.');
    }
  }, []);

  const decide = useCallback(
    async (decision: 'approved' | 'piloting' | 'rejected') => {
      if (!open) return;

      // The server refuses a rejection without a reason; saying so here saves
      // a round trip and explains why it matters.
      if (decision === 'rejected' && note.trim().length < 10) {
        setError(
          'A rejection needs a reason. It is recorded against the agent so the same ' +
            'recommendation is not made again next month.'
        );
        return;
      }

      setBusy(true);
      setError(null);
      try {
        await cardsService.decide(open.reference, decision, note.trim() || undefined);
        setOpen(null);
        setNote('');
        setLoading(true);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The decision was not recorded.');
      } finally {
        setBusy(false);
      }
    },
    [open, note, load]
  );

  const totalAtStake = cards.reduce((sum, c) => sum + c.annualValueEgp, 0);

  return (
    <AppShell
      title="Decision Inbox"
      sub={`${cards.length} waiting · EGP ${formatEgp(totalAtStake)} at stake`}
    >
      <PageHead
        eyebrow="Decide"
        title="What is waiting, most valuable first"
        lede="Each card carries a sized impact, the arithmetic behind it, a named approver, and the metric that will decide in ninety days whether it worked. A card missing any of those could not have been submitted."
      />

      {error && (
        <Note style={{ marginBottom: 12 }}>
          <strong>{error}</strong>
        </Note>
      )}

      {loading && <Card><p>Loading the inbox…</p></Card>}

      {!loading && cards.length === 0 && (
        <Card title="Nothing waiting">
          <p>
            No recommendations are pending a decision. Cards appear here when an
            agent produces one and it passes the completeness gate — a card without
            a sized impact, a control metric or an owner cannot be submitted.
          </p>
        </Card>
      )}

      {!loading &&
        cards.map((c) => (
          <Card
            key={c.reference}
            title={`${c.reference} · ${c.recommendation.slice(0, 70)}${
              c.recommendation.length > 70 ? '…' : ''
            }`}
            sub={c.problem}
            right={
              <button className="btn btn-p" onClick={() => void openCard(c.reference)}>
                Open
              </button>
            }
          >
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Split label="Worth per year" value={`EGP ${formatEgp(c.annualValueEgp)}`} />
              <Split
                label="Confidence"
                value={<Tag tone={CONFIDENCE_TONE[c.confidence] ?? 'n'}>{c.confidence}</Tag>}
              />
              <Split label="Approver" value={c.approverChain.join(' → ')} />
              <Split label="Judged on" value={`${c.controlMetric}, ${c.windowDays} days`} />
            </div>
          </Card>
        ))}

      {open && (
        <Card title={`${open.reference} — the whole card`} className="mt">
          <div className="sec-t">Problem</div>
          <p>{open.problem}</p>

          <div className="sec-t">Root cause</div>
          <p>{open.rootCause}</p>

          <div className="sec-t">Recommendation</div>
          <p>{open.recommendation}</p>

          <div className="sec-t">What it is worth</div>
          <p style={{ fontSize: 22, fontWeight: 700 }}>
            EGP {formatEgp(open.impact.annualValueEgp)} a year
          </p>
          <p style={{ fontSize: 13 }}>{open.impact.summary}</p>

          {/* The arithmetic, so a finance lead can disagree with the working
              rather than with the conclusion. */}
          <div className="sec-t">How that was calculated</div>
          <ol style={{ paddingLeft: 18, fontSize: 13, fontFamily: 'var(--mono, monospace)' }}>
            {open.impact.workings.map((w, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {w}
              </li>
            ))}
          </ol>

          <div className="sec-t">What went into it</div>
          <ul style={{ paddingLeft: 18, fontSize: 13 }}>
            {open.impact.inputs.map((input, i) => (
              <li key={i}>
                <strong>{input.label}:</strong> {input.value.toLocaleString()} {input.unit}{' '}
                <Tag tone={input.kind === 'measured' ? 'g' : 'b'}>{input.kind}</Tag>{' '}
                <span style={{ opacity: 0.7 }}>{input.source}</span>
              </li>
            ))}
          </ul>

          {open.impact.caveats.length > 0 && (
            <Note>
              {open.impact.caveats.map((c, i) => (
                <div key={i}>{c}</div>
              ))}
            </Note>
          )}

          <div className="sec-t">Confidence</div>
          <p style={{ fontSize: 13 }}>
            <Tag tone={CONFIDENCE_TONE[open.confidence.level] ?? 'n'}>
              {open.confidence.level}
            </Tag>{' '}
            from {open.confidence.monthsOfData} months and{' '}
            {open.confidence.recordsExamined.toLocaleString()} records.
          </p>

          <div className="sec-t">Risks and controls</div>
          <ul style={{ paddingLeft: 18, fontSize: 13 }}>
            {open.risksAndControls.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>

          <div className="sec-t">People</div>
          <p style={{ fontSize: 13 }}>{open.peopleImpact}</p>

          <div className="sec-t">How we will know it worked</div>
          <p style={{ fontSize: 13 }}>
            <strong>{open.verification.controlMetricKey}</strong> moves from{' '}
            {open.verification.baselineValue} to {open.verification.targetValue} within{' '}
            {open.verification.windowDays} days.
          </p>
          <Note>
            <strong>Roll back if:</strong> {open.verification.rollbackTrigger}
          </Note>

          <div className="sec-t">Evidence</div>
          <ul style={{ paddingLeft: 18, fontSize: 13 }}>
            {open.evidence.map((e, i) => (
              <li key={i}>
                <Tag tone={e.kind === 'measured' || e.kind === 'tool' ? 'g' : 'b'}>
                  {e.citationId}
                </Tag>{' '}
                {e.statement} <span style={{ opacity: 0.7 }}>({e.source})</span>
              </li>
            ))}
          </ul>

          {open.annotations.length > 0 && (
            <>
              <div className="sec-t">What the team said</div>
              <ul style={{ paddingLeft: 18, fontSize: 13 }}>
                {open.annotations.map((a, i) => (
                  <li key={i}>
                    <Tag tone={a.kind === 'dispute' ? 'r' : 'n'}>{a.kind}</Tag> {a.note}{' '}
                    <span style={{ opacity: 0.7 }}>
                      — {a.author}, {a.createdAt.slice(0, 10)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="sec-t">Your decision</div>
          <textarea
            className="inp"
            style={{ width: '100%', minHeight: 70 }}
            placeholder="A note. Required if you reject — it is recorded against the agent so the same recommendation is not made again."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-p" disabled={busy} onClick={() => void decide('approved')}>
              Approve
            </button>
            <button className="btn btn-g" disabled={busy} onClick={() => void decide('piloting')}>
              Pilot first
            </button>
            <button className="btn btn-g" disabled={busy} onClick={() => void decide('rejected')}>
              Reject
            </button>
            <button className="btn btn-g" disabled={busy} onClick={() => setOpen(null)}>
              Close
            </button>
          </div>
        </Card>
      )}
    </AppShell>
  );
};
